<template>
    <main class="operator-section-menu">
        <header class="operator-section-menu__header">
            <button type="button" title="Voltar" @click="back"><ArrowLeftIcon /></button>
            <div>
                <h1>{{ title }}</h1>
                <p>{{ subtitle }}</p>
            </div>
        </header>

        <section class="operator-section-menu__grid">
            <button
                v-for="item in items"
                :key="item.route"
                type="button"
                class="operator-section-menu__item"
                @click="open(item.route)"
            >
                <span class="operator-section-menu__icon"><component :is="item.icon" /></span>
                <span class="operator-section-menu__copy">
                    <strong>{{ item.title }}</strong>
                    <small>{{ item.subtitle }}</small>
                </span>
                <ChevronRightIcon />
            </button>
        </section>
    </main>
</template>

<script>
import {
    ActivityIcon,
    ArrowLeftIcon,
    BarChart2Icon,
    ChevronRightIcon,
    CpuIcon,
    FileTextIcon,
    GitMergeIcon,
    GridIcon,
    SearchIcon,
    SendIcon,
    StarIcon
} from "vue-feather-icons";

export default {
    components: {
        ActivityIcon,
        ArrowLeftIcon,
        BarChart2Icon,
        ChevronRightIcon,
        CpuIcon,
        FileTextIcon,
        GitMergeIcon,
        GridIcon,
        SearchIcon,
        SendIcon,
        StarIcon
    },
    props: {
        section: {
            type: String,
            required: true
        }
    },
    computed: {
        isResources() {
            return this.section === "resources";
        },
        title() {
            return this.isResources ? "Recursos" : "Ordens";
        },
        subtitle() {
            return this.isResources
                ? "Selecione como deseja acessar os recursos de produção"
                : "Selecione o fluxo de ordens de produção";
        },
        accountId() {
            return this.$store.getters["auth/getAccountId"];
        },
        hasExtrusionModule() {
            if (this.$store.getters["auth/isExtrusionFlowEnabled"]) {
                return true;
            }

            const permissions = this.$store.getters["auth/getPermissions"] || [];
            if (
                Array.isArray(permissions) &&
                permissions.some(p =>
                    typeof p === "string"
                        ? p.includes("extrusion")
                        : p.name && p.name.includes("extrusion")
                )
            ) {
                return true;
            }

            return false;
        },
        items() {
            if (this.isResources) {
                const resourceItems = [
                    {
                        title: "Recursos Favoritos",
                        subtitle: "Visualize seus recursos salvos",
                        route: "operator-favorite-resources",
                        icon: "StarIcon"
                    },
                    {
                        title: "Recursos",
                        subtitle: "Visualize todos os recursos cadastrados",
                        route: "operator-resources",
                        icon: "GridIcon"
                    },
                    {
                        title: "Múltiplas Ordens por Recurso",
                        subtitle: "Gerencie ordens simultâneas no mesmo recurso",
                        route: "operator-multiple-orders",
                        icon: "ActivityIcon"
                    }
                ];

                if (this.hasExtrusionModule) {
                    resourceItems.push({
                        title: "Extrusão e Fornos",
                        subtitle: "Acompanhe e opere as prensas de extrusão e fornos",
                        route: "operator-extrusion-resources",
                        icon: "CpuIcon"
                    });
                }

                return resourceItems;
            }

            const items = [
                {
                    title: "Ordens",
                    subtitle: "Visualize todas as ordens cadastradas",
                    route: "operator-orders-all",
                    icon: "FileTextIcon"
                },
                {
                    title: "Agrupamento de Ordens / Nesting",
                    subtitle: "Monte agrupamentos e faça apontamentos por conjunto",
                    route: "operator-order-groupings",
                    icon: "GitMergeIcon"
                },
                {
                    title: "Código de Barras",
                    subtitle: "Utilize o leitor para escanear ordens",
                    route: "operator-barcode",
                    icon: "SearchIcon"
                },
                {
                    title: "Acompanhamento das Ordens",
                    subtitle: "Acompanhe o status das ordens em produção",
                    route: "operator-order-tracking",
                    icon: "BarChart2Icon"
                }
            ];

            if (this.accountId === 23) {
                items.push({
                    title: "Envios para o SAP",
                    subtitle: "Consulte confirmações e falhas da integração",
                    route: "operator-sap-integration-report",
                    icon: "SendIcon"
                });
            }

            return items;
        }
    },
    created() {
        this.$store.dispatch("auth/fetchPermissions");
    },
    methods: {
        back() {
            this.$router.push({ name: "operator", params: { prefix: this.$route.params.prefix } });
        },
        open(route) {
            this.$router.push({ name: route, params: { prefix: this.$route.params.prefix } });
        }
    }
};
</script>

<style lang="scss" scoped>
.operator-section-menu {
    width: 100%;
    color: #4c4541;

    &__header {
        display: flex;
        align-items: center;
        gap: 14px;
        margin-bottom: 24px;

        button {
            width: 38px;
            height: 38px;
            border: 1px solid #cfc4be;
            border-radius: 6px;
            background: #fff;
            color: #974900;
        }

        h1 {
            margin: 0;
            font-family: "Raleway";
            font-size: 24px;
            font-weight: 600;
        }

        p {
            margin: 3px 0 0;
            color: #998f8a;
            font-size: 13px;
        }
    }

    &__grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
    }

    &__item {
        min-height: 112px;
        display: grid;
        grid-template-columns: 48px minmax(0, 1fr) 20px;
        align-items: center;
        gap: 16px;
        padding: 18px;
        border: 1px solid #cfc4be;
        border-radius: 8px;
        background: #fafafa;
        color: #4c4541;
        text-align: left;
        box-shadow: 0 4px 18px rgba(58, 46, 38, 0.08);

        &:hover,
        &:focus-visible {
            border-color: #974900;
            background: #ffede2;
            outline: none;
        }
    }

    &__icon {
        width: 48px;
        height: 48px;
        display: grid;
        place-items: center;
        border: 1px solid #974900;
        border-radius: 6px;
        color: #974900;
    }

    &__copy {
        min-width: 0;

        strong,
        small {
            display: block;
        }

        strong {
            font-family: "Raleway";
            font-size: 16px;
            font-weight: 600;
        }

        small {
            margin-top: 4px;
            color: #998f8a;
            font-size: 12px;
        }
    }
}

@media (max-width: 700px) {
    .operator-section-menu {
        &__grid {
            grid-template-columns: 1fr;
        }

        &__item {
            min-height: 96px;
        }
    }
}
</style>
